import React, { useMemo, ButtonHTMLAttributes } from 'react';
import './button.css';
import { LoadingSpinnerIcon } from '../Icons';

interface CareMapprButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  id?: string;
  extraClassNames?: string;
  label?: string;
  labelClassName?: string;
  onClick?: (e: React.MouseEvent) => void;
  variant?: 'primary' | 'secondary' | 'link' | 'transparent';
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  contentAlign?: 'start' | 'end' | 'center';
  hoverWithColor?: boolean;
  children?: React.ReactNode;
  isLoading?: boolean;
}

const CareMapprButton: React.FC<CareMapprButtonProps> = ({
  extraClassNames = '',
  label = '',
  labelClassName = '',
  onClick,
  variant = 'primary',
  endIcon = null,
  startIcon = null,
  contentAlign = 'center',
  hoverWithColor = true,
  children = null,
  isLoading = false,
  disabled = false,
  ...props
}) => {
  const contentAlignment = useMemo(() => {
    switch (contentAlign) {
      case 'start':
        return 'justify-content-start';
      case 'end':
        return 'justify-content-end';
      case 'center':
        return 'justify-content-center';
      default:
        return 'justify-content-center';
    }
  }, [contentAlign]);

  const buttonVariant = useMemo(() => {
    switch (variant) {
      case 'primary':
        return 'caremappr-btn btnPrimary';
      case 'secondary':
        return 'caremappr-btn btnSecondary';
      case 'link':
        return 'caremappr-btn btnLink';
      case 'transparent':
        return 'transparent-btn caremappr-btn';
      default:
        return 'caremappr-btn btnPrimary';
    }
  }, [variant]);

  const defaultFontSize = labelClassName ? {} : { fontSize: '14px' };

  return (
    <button
      {...props}
      className={`btn align-items-center ${label && (startIcon || endIcon) ? 'gap-2' : ''} ${contentAlignment} ${buttonVariant} ${
        variant === 'transparent' && hoverWithColor ? 'icon-button' : ''
      } ${extraClassNames || ''}`}
      onClick={onClick}
      disabled={isLoading || disabled}
    >
      {isLoading ? (
        <span className="spinner-animation" aria-hidden="true">
          <LoadingSpinnerIcon />
        </span>
      ) : (
        <>
          {startIcon && <span className="icon-start">{startIcon}</span>}
          {label ? (
            <span style={defaultFontSize} className={labelClassName}>
              {label}
            </span>
          ) : null}
          {children}
          {endIcon && <span className="icon-end">{endIcon}</span>}
        </>
      )}
    </button>
  );
};

export default CareMapprButton;
